import {upperFirst} from "@pg-mono/nodash";
import {rpAppLink} from "@pg-mono/rp-routes";
import {areaFormat, dateTimeFormat, formatPL, numberFormat, pluralize, priceFormat, quarterToRomanNumerals} from "@pg-mono/string-utils";

import {Country} from "../../../../region/types/Country";
import {RegionType} from "../../../../region/types/RegionType";
import {
    ILocationGenericParagraph,
    ILocationGenericSection,
    ILocationGenericSectionProps,
    ILocationGenericSubsection,
    ParagraphType,
    TextType
} from "./constants";

const formatQuarterWithYear = (dateStr: string): string => {
    const date = new Date(dateStr);
    return quarterToRomanNumerals(formatPL(date, dateTimeFormat.quarterLong), "kwartał");
};

export const flatOffersLocationGenericDescription = (props: ILocationGenericSectionProps): ILocationGenericSection[] => {
    const variables = {
        regionName: props.region.name,
        regionSlug: props.region.slug,
        isCity: props.region.type === RegionType.CITY,
        regionNameDeclensionAbout: props.region.name_declension_about,
        regionNameDeclensionWhere: props.region.name_declension_where,
        regionPropertiesCountForSale: props.region.stats?.properties_all_count_for_sale || 0,
        regionOfferCountForSale: props.region.stats?.offers_count_for_sale || 0,
        propertiesFlatsRangesAreaMin: props.region.stats?.properties_flats_ranges_area_min || 0,
        propertiesFlatsRangesAreaMax: props.region.stats?.properties_flats_ranges_area_max || 0,
        promotionsCount: props.region.stats?.promotions_count_total || 0,
        averagePriceM2: props.region.stats?.average_price_m2 || 0,
        averagePriceM2Y2YChange: props.region.stats?.average_price_m2_y2y_change ?? null,
        flatPriceMin: props.region.stats?.properties_flats_ranges_price_min || 0,
        flatsCountReady: props.region.stats?.properties_flats_count_ready || 0,
        flatsCountReadyInThreeMonths: props.region.stats?.properties_flats_count_ready_in_3m || 0,
        flatsCountReadyInSixMonths: props.region.stats?.properties_flats_count_ready_in_6m || 0,
        flatsCountReadyInOneYear: props.region.stats?.properties_flats_count_ready_in_12m || 0,
        flatsGroundFloor: props.region.stats?.properties_flats_ground_floor || 0,
        flatsGroundFloorWithGarden: props.region.stats?.properties_flats_ground_floor_with_garden || 0,
        flatsLowFloor: props.region.stats?.properties_flats_low_floor || 0,
        flatsHighFloor: props.region.stats?.properties_flats_high_floor || 0,
        flatsLastFloor: props.region.stats?.properties_flats_last_floor || 0,
        flatsLastFloorWithTerrace: props.region.stats?.properties_flats_last_floor_with_terrace || 0,
        flatsLatestConstructionDate: props.region.stats?.properties_flats_latest_construction_date || null,
        flatsOffersCountForSale: props.region.stats?.offers_flats_count_for_sale || 0,
        flatsPropertiesCountForSale: props.region.stats?.properties_flats_count_for_sale || 0,
        flatsCountByDistrict: props.region.stats?.properties_flats_count_by_district || {},
        flatsCountByPrice: props.region.stats?.properties_flats_count_by_price || {},
        flatsAreaByRooms: props.region.stats?.properties_flats_ranges_area_by_rooms || {},
        flatsPriceByRooms: props.region.stats?.properties_flats_ranges_price_by_rooms || {},
        flatsPriceM2ByRooms: props.region.stats?.properties_flats_ranges_price_m2_by_rooms || {},
        housesPropertiesCountForSale: props.region.stats?.properties_houses_count_for_sale || 0,
        flatsRoomsRanges: props.region.stats?.properties_flats_rooms_ranges || {}
    };

    if (!variables.flatsOffersCountForSale || !variables.flatsPropertiesCountForSale) {
        return [];
    }

    if (props.region.country === Country.SPAIN) {
        return [
            {
                headline: `${variables.regionName} - mieszkanie w Hiszpanii`,
                paragraphs: [
                    {
                        type: ParagraphType.TEXT,
                        text: `W ${upperFirst(variables.regionName)} ${pluralize([
                            `dostępna jest ${numberFormat(variables.regionPropertiesCountForSale)} oferta mieszkania.`,
                            `dostępne są ${numberFormat(variables.regionPropertiesCountForSale)} oferty mieszkań.`,
                            `dostępnych jest ${numberFormat(variables.regionPropertiesCountForSale)} ofert mieszkań.`
                        ])(variables.regionPropertiesCountForSale)} Apartamenty w ${variables.regionName} mają powierzchnie ${
                            variables.propertiesFlatsRangesAreaMin !== variables.propertiesFlatsRangesAreaMax
                                ? pluralize([
                                      `${areaFormat(variables.propertiesFlatsRangesAreaMin)}`,
                                      `od ${variables.propertiesFlatsRangesAreaMin} do ${areaFormat(variables.propertiesFlatsRangesAreaMax)}`,
                                      `od ${variables.propertiesFlatsRangesAreaMin} do ${areaFormat(variables.propertiesFlatsRangesAreaMax)}`
                                  ])(variables.regionPropertiesCountForSale)
                                : `od ${areaFormat(variables.propertiesFlatsRangesAreaMin)}`
                        }.`
                    }
                ]
            },
            {
                headline: `Ile kosztuje nowe mieszkanie w Hiszpanii w ${variables.regionName}?`,
                paragraphs: [
                    {
                        type: ParagraphType.TEXT,
                        text: [
                            `Najtańsze mieszkanie w ${variables.regionName} w sprzedaży kosztuje ${priceFormat(variables.flatPriceMin, {unit: "euro"})}.`,
                            variables.averagePriceM2 > 0
                                ? `Średnia cena za m2 mieszkania w ${variables.regionName} wynosi ${priceFormat(variables.averagePriceM2, {unit: "euro"})}.`
                                : ""
                        ]
                    },
                    {
                        type: ParagraphType.TEXT,
                        text: [
                            `Jesteś zainteresowany zakupem domu w Hiszpanii? Poznaj `,
                            variables.housesPropertiesCountForSale > 0
                                ? {
                                      type: TextType.LINK,
                                      href: rpAppLink.offer.listFriendlyAbroad({
                                          friendlySlug: `nowe-domy`,
                                          country: `hiszpania`,
                                          abroadRegionSlug: `${variables.regionSlug}`
                                      }),
                                      text: `ofertę domów w ${variables.regionName}.`
                                  }
                                : `ofertę domów ${variables.regionName}.`
                        ]
                    }
                ]
            }
        ];
    }

    const now = new Date();
    const currentMonth = formatPL(now, "LLLL");
    const currentYear = now.getFullYear();
    const monthYear = `${currentMonth} ${currentYear}`;
    const updateDate = formatPL(now, "dd.MM.yyyy");

    // Section 1: Available offer
    const availableOfferSubsection = getAvailableOfferSubsection(variables, monthYear);

    // Section 2: Prices and available sizes
    const pricesSubsection = getPricesSubsection(variables, monthYear);

    // Section 3: Delivery dates
    const deliverySubsection = getDeliverySubsection(variables, monthYear);

    // Section 4: Price ranges
    const priceRangesSubsection = getPriceRangesSubsection(variables, monthYear);

    // Section 5: Geographic distribution
    const geographicDistributionSubsection = getGeographicDistributionSubsection(variables, monthYear);

    // Section 6: Promotions
    const promotionsSubsection = getPromotionsSubsection(variables);

    const subsections: ILocationGenericSubsection[] = [
        availableOfferSubsection,
        pricesSubsection,
        deliverySubsection,
        ...(priceRangesSubsection ? [priceRangesSubsection] : []),
        ...(geographicDistributionSubsection ? [geographicDistributionSubsection] : []),
        promotionsSubsection
    ];

    return [
        {
            headline: `Raport rynkowy – mieszkania ${variables.regionName}, ${monthYear}`,
            description: `Sprawdź aktualny stan rynku nowych mieszkań ${variables.regionNameDeclensionWhere}: liczba ofert, ceny, metraże oraz terminy oddania inwestycji deweloperskich.`,
            updateDate,
            paragraphs: [],
            subsections
        }
    ];
};

interface IFlatVariables {
    regionName: string;
    regionSlug: string;
    isCity: boolean;
    regionNameDeclensionAbout: string;
    regionNameDeclensionWhere: string;
    regionPropertiesCountForSale: number;
    regionOfferCountForSale: number;
    propertiesFlatsRangesAreaMin: number;
    propertiesFlatsRangesAreaMax: number;
    promotionsCount: number;
    averagePriceM2: number;
    averagePriceM2Y2YChange: number | null;
    flatPriceMin: number;
    flatsCountReady: number;
    flatsCountReadyInThreeMonths: number;
    flatsCountReadyInSixMonths: number;
    flatsCountReadyInOneYear: number;
    flatsGroundFloor: number;
    flatsGroundFloorWithGarden: number;
    flatsLowFloor: number;
    flatsHighFloor: number;
    flatsLastFloor: number;
    flatsLastFloorWithTerrace: number;
    flatsLatestConstructionDate: string | null;
    flatsOffersCountForSale: number;
    flatsPropertiesCountForSale: number;
    flatsCountByDistrict: Record<string, IFlatDistrictStats>;
    flatsCountByPrice: Record<string, number>;
    flatsAreaByRooms: Record<string, IFlatRangeStats>;
    flatsPriceByRooms: Record<string, IFlatRangeStats>;
    flatsPriceM2ByRooms: Record<string, IFlatRangeStats>;
    housesPropertiesCountForSale: number;
    flatsRoomsRanges: Record<number, number>;
}

interface IFlatRangeStats {
    avg?: number;
    max: number;
    min: number;
}

interface IFlatDistrictStats {
    avg_price_m2: number;
    count: number;
}

const getPriceTableRoomLabel = (roomCount: number): string => {
    switch (roomCount) {
        case 1:
            return "Kawalerki";
        case 2:
            return "2 pokoje";
        case 3:
            return "3 pokoje";
        case 4:
            return "4 pokoje";
        case 5:
            return "5+ pokoi";
        default:
            return `${roomCount} pokoi`;
    }
};

const getRoomTypeLabel = (roomCount: number): string => {
    switch (roomCount) {
        case 1:
            return "Kawalerki";
        case 2:
            return "Mieszkania 2-pokojowe";
        case 3:
            return "Mieszkania 3-pokojowe";
        case 4:
            return "Mieszkania 4-pokojowe";
        case 5:
            return "Mieszkania 5-pokojowe i większe";
        default:
            return `Mieszkania ${roomCount}-pokojowe`;
    }
};

const isFiniteNumber = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);

const getAveragePriceM2Y2YChangeText = (variables: IFlatVariables): string | null => {
    if (!variables.isCity || !isFiniteNumber(variables.averagePriceM2Y2YChange)) {
        return null;
    }

    if (variables.averagePriceM2Y2YChange === 0) {
        return `Zmiana rok do roku: Ceny nowych mieszkań ${variables.regionNameDeclensionWhere} nie zmieniły się w porównaniu rok do roku.`;
    }

    return `Zmiana rok do roku: Ceny nowych mieszkań ${variables.regionNameDeclensionWhere} ${
        variables.averagePriceM2Y2YChange > 0 ? "wzrosły" : "spadły"
    } o ${numberFormat(Math.abs(variables.averagePriceM2Y2YChange), {precision: 2})} % w porównaniu rok do roku.`;
};

const ROOM_PRICE_TABLE_ROOM_COUNTS = [1, 2, 3, 4, 5];
const MIN_GROUPED_ROOM_COUNT = 5;

const getRoomRangeStats = (record: Record<string, IFlatRangeStats>, roomCount: number): IFlatRangeStats | null => {
    const roomKey = roomCount === 5 ? "5+" : String(roomCount);
    const value = record[roomKey as keyof typeof record];

    if (value && isFiniteNumber(value.avg) && isFiniteNumber(value.max) && isFiniteNumber(value.min)) {
        return value;
    }

    return null;
};

const getRoomRangeCount = (roomRanges: Record<number, number>, roomCount: number): number => {
    if (roomCount < MIN_GROUPED_ROOM_COUNT) {
        return roomRanges[roomCount] || 0;
    }

    return Object.entries(roomRanges).reduce((totalCount, [roomRange, count]) => {
        const parsedRoomRange = Number(roomRange);

        if (!isFiniteNumber(count) || !Number.isFinite(parsedRoomRange) || parsedRoomRange < MIN_GROUPED_ROOM_COUNT) {
            return totalCount;
        }

        return totalCount + count;
    }, 0);
};

const getPricesByRoomRows = (variables: IFlatVariables): string[][] => {
    const rows = ROOM_PRICE_TABLE_ROOM_COUNTS.map((roomCount) => {
        const areaStats = getRoomRangeStats(variables.flatsAreaByRooms, roomCount);
        const priceM2Stats = getRoomRangeStats(variables.flatsPriceM2ByRooms, roomCount);
        const priceStats = getRoomRangeStats(variables.flatsPriceByRooms, roomCount);

        if (!areaStats?.avg || !priceM2Stats?.avg || !priceStats?.min || !priceStats?.avg) {
            return null;
        }

        return [
            getPriceTableRoomLabel(roomCount),
            String(numberFormat(areaStats.avg, {precision: 2})),
            String(numberFormat(priceM2Stats.avg)),
            String(numberFormat(priceStats.min)),
            String(numberFormat(priceStats.avg))
        ];
    }).filter(Boolean) as string[][];

    return rows.length === ROOM_PRICE_TABLE_ROOM_COUNTS.length ? rows : [];
};

const getAvailableOfferSubsection = (variables: IFlatVariables, monthYear: string): ILocationGenericSubsection => {
    const roomRanges = variables.flatsRoomsRanges;
    const totalFlats = variables.flatsPropertiesCountForSale;

    const roomEntries = [1, 2, 3, 4, 5].map((room) => {
        const count = getRoomRangeCount(roomRanges, room);

        return {
            room,
            count,
            percent: totalFlats > 0 ? ((count / totalFlats) * 100).toFixed(1) : "0.0"
        };
    });

    const hasRoomEntries = roomEntries.some((entry) => entry.count > 0);
    const dominantRoom = roomEntries.reduce((max, entry) => (entry.count > max.count ? entry : max), {room: 0, count: 0, percent: "0.0"});

    const paragraphs: ILocationGenericParagraph[] = [
        {
            type: ParagraphType.TEXT,
            text: `${upperFirst(variables.regionNameDeclensionWhere)} ${pluralize([
                `dostępna jest w sprzedaży ${numberFormat(variables.flatsPropertiesCountForSale)} oferta nowych mieszkań znajdująca`,
                `dostępne są w sprzedaży ${numberFormat(variables.flatsPropertiesCountForSale)} oferty nowych mieszkań znajdujące`,
                `dostępnych jest w sprzedaży ${numberFormat(variables.flatsPropertiesCountForSale)} ofert nowych mieszkań znajdujących`
            ])(variables.flatsPropertiesCountForSale)} się w ${pluralize([
                `${numberFormat(variables.flatsOffersCountForSale)} inwestycji deweloperskiej`,
                `${numberFormat(variables.flatsOffersCountForSale)} inwestycjach deweloperskich`,
                `${numberFormat(variables.flatsOffersCountForSale)} inwestycjach deweloperskich`
            ])(variables.flatsOffersCountForSale)}.`
        }
    ];

    if (dominantRoom.room > 0) {
        paragraphs.push({
            type: ParagraphType.TEXT,
            text: `W ofercie deweloperów ${variables.regionNameDeclensionWhere} dominują mieszkania ${dominantRoom.room === 1 ? "jednopokojowe" : `${dominantRoom.room}-pokojowe`}, które stanowią ${dominantRoom.percent} % wszystkich mieszkań.`
        });
    }

    if (hasRoomEntries) {
        paragraphs.push({
            type: ParagraphType.TABLE,
            caption: `Struktura oferty wg typu mieszkania – ${variables.regionName}, ${monthYear}`,
            captionHidden: "(dane z RynekPierwotny.pl)",
            headers: ["Typ mieszkania", "Liczba lokali", "Procent oferty deweloperów"],
            rows: roomEntries.map((entry) => [getRoomTypeLabel(entry.room), String(numberFormat(entry.count)), `${entry.percent} %`])
        });
    }

    return {
        headline: "Dostępna oferta mieszkań",
        paragraphs
    };
};

const getPricesSubsection = (variables: IFlatVariables, monthYear: string): ILocationGenericSubsection => {
    const paragraphs: ILocationGenericParagraph[] = [];
    const textParts: string[] = [];

    if (variables.flatPriceMin > 0) {
        textParts.push(`Najniższa cena: Najtańsze nowe mieszkanie ${variables.regionNameDeclensionWhere} kosztuje ${priceFormat(variables.flatPriceMin)}.`);
    }

    if (variables.averagePriceM2 > 0) {
        textParts.push(
            `Średnia cena za mkw.: Cena metra kwadratowego mieszkania na rynku pierwotnym ${variables.regionNameDeclensionWhere} wynosi ${priceFormat(variables.averagePriceM2)}.`
        );
    }

    const averagePriceM2Y2YChangeText = getAveragePriceM2Y2YChangeText(variables);

    if (averagePriceM2Y2YChangeText) {
        textParts.push(averagePriceM2Y2YChangeText);
    }

    if (variables.propertiesFlatsRangesAreaMin > 0 && variables.propertiesFlatsRangesAreaMax > 0) {
        textParts.push(
            variables.propertiesFlatsRangesAreaMin !== variables.propertiesFlatsRangesAreaMax
                ? `Rozpiętość metraży: Metraże lokali zaczynają się od ${areaFormat(variables.propertiesFlatsRangesAreaMin)} i sięgają ${areaFormat(variables.propertiesFlatsRangesAreaMax)}.`
                : `Rozpiętość metraży: Metraże lokali wynoszą ${areaFormat(variables.propertiesFlatsRangesAreaMin)}.`
        );
    }

    if (textParts.length > 0) {
        paragraphs.push({
            type: ParagraphType.LIST,
            text: textParts
        });
    }

    const pricesByRoomRows = getPricesByRoomRows(variables);

    if (pricesByRoomRows.length > 0) {
        paragraphs.push({
            type: ParagraphType.TABLE,
            caption: `Ceny i metraże nowych mieszkań według liczby pokoi – ${variables.regionName}, ${monthYear}`,
            captionHidden: "(dane z RynekPierwotny.pl)",
            headers: ["Liczba pokoi", "Średni metraż (mkw.)", "Średnia cena za mkw. (zł)", "Cena min. (zł)", "Cena średnia (zł)"],
            rows: pricesByRoomRows
        });
    }

    return {
        headline: "Ceny i dostępne metraże",
        paragraphs
    };
};

const getDeliverySubsection = (variables: IFlatVariables, monthYear: string): ILocationGenericSubsection => {
    const paragraphs: ILocationGenericParagraph[] = [];

    const totalInConstruction = variables.flatsCountReadyInThreeMonths + variables.flatsCountReadyInSixMonths + variables.flatsCountReadyInOneYear;

    if (variables.flatsCountReady > 0) {
        paragraphs.push({
            type: ParagraphType.TEXT,
            text: `Aktualnie ${variables.regionNameDeclensionWhere} ${pluralize([
                `dostępna jest ${numberFormat(variables.flatsCountReady)} oferta`,
                `dostępne są ${numberFormat(variables.flatsCountReady)} oferty`,
                `dostępnych jest ${numberFormat(variables.flatsCountReady)} ofert`
            ])(variables.flatsCountReady)} nowych mieszkań gotowych do odbioru.`
        });
    } else {
        paragraphs.push({
            type: ParagraphType.TEXT,
            text: `Obecnie w ofercie deweloperów ${variables.regionNameDeclensionWhere} nie ma mieszkań oddanych do użytku.${
                variables.flatsLatestConstructionDate
                    ? ` Najpóźniejszy planowany termin oddania inwestycji do użytkowania to koniec ${formatQuarterWithYear(variables.flatsLatestConstructionDate)}.`
                    : ""
            }`
        });
    }

    if (totalInConstruction > 0) {
        paragraphs.push({
            type: ParagraphType.TEXT,
            text: `W trakcie budowy ${pluralize([
                `jest ${numberFormat(totalInConstruction)} mieszkanie`,
                `są ${numberFormat(totalInConstruction)} mieszkania`,
                `jest ${numberFormat(totalInConstruction)} mieszkań`
            ])(totalInConstruction)}.${
                variables.flatsLatestConstructionDate
                    ? ` Najpóźniejszy planowany termin oddania inwestycji do użytkowania to koniec ${formatQuarterWithYear(variables.flatsLatestConstructionDate)}.`
                    : ""
            }`
        });
    }

    const deliveryRows: string[][] = [];

    if (variables.flatsCountReady > 0) {
        deliveryRows.push(["Gotowe do odbioru", String(numberFormat(variables.flatsCountReady))]);
    }
    if (variables.flatsCountReadyInSixMonths > 0) {
        deliveryRows.push(["Oddanie do 6 miesięcy", String(numberFormat(variables.flatsCountReadyInSixMonths))]);
    }
    if (variables.flatsCountReadyInOneYear > 0) {
        deliveryRows.push(["Oddanie od 6 do 12 miesięcy", String(numberFormat(variables.flatsCountReadyInOneYear - variables.flatsCountReadyInSixMonths))]);
    }

    const beyondOneYear = variables.flatsPropertiesCountForSale - variables.flatsCountReady - variables.flatsCountReadyInOneYear;
    if (beyondOneYear > 0) {
        deliveryRows.push(["Oddanie powyżej roku", String(numberFormat(beyondOneYear))]);
    }

    if (deliveryRows.length > 0) {
        paragraphs.push({
            type: ParagraphType.TABLE,
            caption: `Dostępność mieszkań według terminu oddania – ${variables.regionName}, ${monthYear}`,
            captionHidden: "(dane z RynekPierwotny.pl)",
            headers: ["Termin oddania do użytku", "Liczba lokali"],
            rows: deliveryRows
        });
    }

    return {
        headline: "Terminy oddania do użytku",
        paragraphs
    };
};

interface ICountEntry {
    label: string;
    count: number;
    order: number;
}

interface IDistrictEntry {
    avgPriceM2: number;
    count: number;
    label: string;
}

const PRICE_RANGES = [
    {
        label: "Poniżej 400 000 zł",
        keys: ["up400k", "up400000", "up_400000", "0-400000", "0_400000", "below_400000", "less_than_400000", "lt_400000", "ponizej_400000"]
    },
    {label: "400 001 – 600 000 zł", keys: ["400k600k", "400001-600000", "400001_600000", "400000-600000", "400000_600000"]},
    {label: "600 001 – 800 000 zł", keys: ["600k800k", "600001-800000", "600001_800000", "600000-800000", "600000_800000"]},
    {label: "800 001 – 1 000 000 zł", keys: ["800k1m", "800001-1000000", "800001_1000000", "800000-1000000", "800000_1000000"]},
    {
        label: "Powyżej 1 000 000 zł",
        keys: [
            "over1m",
            "over1000000",
            "over_1000000",
            "1000001-",
            "1000001_plus",
            "1000000-",
            "1000000_plus",
            "above_1000000",
            "gt_1000000",
            "powyzej_1000000"
        ]
    }
];

const CUMULATIVE_PRICE_THRESHOLDS = [
    {threshold: 400000, keys: ["up400k", "up400000", "up_400000"]},
    {threshold: 600000, keys: ["up600k", "up600000", "up_600000"]},
    {threshold: 800000, keys: ["up800k", "up800000", "up_800000"]},
    {threshold: 1000000, keys: ["up1m", "up1000k", "up1000000", "up_1000000"]}
] as const;

const CUMULATIVE_PRICE_RANGES = [
    {label: "400 001 – 600 000 zł", lowerThreshold: 400000, upperThreshold: 600000},
    {label: "600 001 – 800 000 zł", lowerThreshold: 600000, upperThreshold: 800000},
    {label: "800 001 – 1 000 000 zł", lowerThreshold: 800000, upperThreshold: 1000000}
];

const normalizeCountKey = (key: string): string =>
    key
        .toLowerCase()
        .replace(/[–—]/g, "-")
        .replace(/\u00a0/g, " ")
        .replace(/\s+/g, "")
        .replace(/zł/g, "zl")
        .replace(/więcej/g, "plus")
        .replace(/powyżej/g, "powyzej")
        .replace(/poniżej/g, "ponizej");

const getPriceRangeLabel = (key: string): string | null => {
    const normalizedKey = normalizeCountKey(key);
    const priceRange = PRICE_RANGES.find(
        (range) => normalizeCountKey(range.label) === normalizedKey || range.keys.some((rangeKey) => normalizeCountKey(rangeKey) === normalizedKey)
    );

    return priceRange?.label || null;
};

const getPriceRangeOrder = (label: string): number => {
    const index = PRICE_RANGES.findIndex((range) => range.label === label || normalizeCountKey(range.label) === normalizeCountKey(label));

    return index === -1 ? Number.MAX_SAFE_INTEGER : index;
};

const getCumulativePriceThresholdCount = (counts: Record<string, number>, threshold: number): number | null => {
    const thresholdConfig = CUMULATIVE_PRICE_THRESHOLDS.find((priceThreshold) => priceThreshold.threshold === threshold);

    if (!thresholdConfig) {
        return null;
    }

    const normalizedThresholdKeys = thresholdConfig.keys.map(normalizeCountKey);
    const thresholdEntry = Object.entries(counts).find(
        ([key, count]) => normalizedThresholdKeys.includes(normalizeCountKey(key)) && isFiniteNumber(count) && count >= 0
    );

    return thresholdEntry?.[1] ?? null;
};

const sortCountEntries = (entries: ICountEntry[]): ICountEntry[] =>
    entries.sort((firstEntry, secondEntry) => {
        if (firstEntry.order !== secondEntry.order) {
            return firstEntry.order - secondEntry.order;
        }

        return firstEntry.label.localeCompare(secondEntry.label, "pl");
    });

const getCountEntries = (counts: Record<string, number>, getLabel: (key: string) => string | null, getOrder: (label: string) => number): ICountEntry[] => {
    const entries = new Map<string, ICountEntry>();

    Object.entries(counts).forEach(([key, count]) => {
        if (!isFiniteNumber(count) || count < 0) {
            return;
        }

        const label = getLabel(key);

        if (!label) {
            return;
        }

        const currentEntry = entries.get(label);

        entries.set(label, {
            label,
            count: (currentEntry?.count || 0) + count,
            order: getOrder(label)
        });
    });

    return sortCountEntries(Array.from(entries.values()));
};

const getPriceRangeEntries = (counts: Record<string, number>): ICountEntry[] => {
    const entriesByLabel = new Map(getCountEntries(counts, getPriceRangeLabel, getPriceRangeOrder).map((entry) => [entry.label, entry]));

    CUMULATIVE_PRICE_RANGES.forEach((priceRange) => {
        if (entriesByLabel.has(priceRange.label)) {
            return;
        }

        const lowerCount = getCumulativePriceThresholdCount(counts, priceRange.lowerThreshold);
        const upperCount = getCumulativePriceThresholdCount(counts, priceRange.upperThreshold);

        if (lowerCount === null || upperCount === null || upperCount < lowerCount) {
            return;
        }

        entriesByLabel.set(priceRange.label, {
            label: priceRange.label,
            count: upperCount - lowerCount,
            order: getPriceRangeOrder(priceRange.label)
        });
    });

    return sortCountEntries(Array.from(entriesByLabel.values()));
};

const getPriceRangesSubsection = (variables: IFlatVariables, monthYear: string): ILocationGenericSubsection | null => {
    const priceRangeEntries = getPriceRangeEntries(variables.flatsCountByPrice);

    if (priceRangeEntries.length === 0) {
        return null;
    }

    const paragraphs: ILocationGenericParagraph[] = [];
    const mostPopularRange = priceRangeEntries.reduce<ICountEntry | null>(
        (currentMostPopularRange, priceRangeEntry) =>
            currentMostPopularRange && currentMostPopularRange.count >= priceRangeEntry.count ? currentMostPopularRange : priceRangeEntry,
        null
    );

    paragraphs.push({
        type: ParagraphType.TABLE,
        caption: `Liczba mieszkań według przedziałów cenowych – ${variables.regionName}, ${monthYear}`,
        captionHidden: "(dane z RynekPierwotny.pl)",
        headers: ["Zakres cenowy", "Liczba lokali"],
        rows: priceRangeEntries.map((priceRangeEntry) => [priceRangeEntry.label, String(numberFormat(priceRangeEntry.count))])
    });

    if (mostPopularRange && mostPopularRange.count > 0) {
        paragraphs.push({
            type: ParagraphType.TEXT,
            text: `Najwięcej ofert nowych mieszkań ${variables.regionNameDeclensionWhere} jest dostępnych w przedziale ${mostPopularRange.label}.`
        });
    }

    return {
        headline: "Rozkład oferty według przedziałów cenowych",
        paragraphs
    };
};

const getDistrictEntries = (districts: Record<string, IFlatDistrictStats>): IDistrictEntry[] =>
    Object.entries(districts)
        .filter(([, districtStats]) => isFiniteNumber(districtStats.count) && districtStats.count > 0 && isFiniteNumber(districtStats.avg_price_m2))
        .map(([districtName, districtStats]) => ({
            avgPriceM2: districtStats.avg_price_m2,
            count: districtStats.count,
            label: districtName
        }))
        .sort((firstEntry, secondEntry) => firstEntry.label.localeCompare(secondEntry.label, "pl"));

const getGeographicDistributionSubsection = (variables: IFlatVariables, monthYear: string): ILocationGenericSubsection | null => {
    const districtEntries = getDistrictEntries(variables.flatsCountByDistrict);

    if (districtEntries.length === 0) {
        return null;
    }

    const paragraphs: ILocationGenericParagraph[] = [];
    const topDistrict = districtEntries.reduce((currentTopDistrict, districtEntry) =>
        currentTopDistrict.count >= districtEntry.count ? currentTopDistrict : districtEntry
    );
    const cheapestDistrict = districtEntries.reduce((currentCheapestDistrict, districtEntry) =>
        currentCheapestDistrict.avgPriceM2 <= districtEntry.avgPriceM2 ? currentCheapestDistrict : districtEntry
    );

    paragraphs.push({
        type: ParagraphType.TEXT,
        text: `Największy wybór nowych lokali ${variables.regionNameDeclensionWhere} deweloperzy oferują w dzielnicy ${topDistrict.label}, gdzie ${pluralize([
            `dostępna jest ${numberFormat(topDistrict.count)} oferta`,
            `dostępne są ${numberFormat(topDistrict.count)} oferty`,
            `dostępnych jest ${numberFormat(topDistrict.count)} ofert`
        ])(topDistrict.count)}. Najniższa średnia cena obowiązuje w dzielnicy ${cheapestDistrict.label}.`
    });

    paragraphs.push({
        type: ParagraphType.TABLE,
        caption: `Liczba ofert i średnie ceny według dzielnic – ${variables.regionName}, ${monthYear}`,
        captionHidden: "(dane z RynekPierwotny.pl)",
        headers: ["Lokalizacja inwestycji", "Liczba lokali", "Średnia cena zł/mkw."],
        rows: districtEntries.map((districtEntry) => [
            districtEntry.label,
            String(numberFormat(districtEntry.count)),
            String(numberFormat(districtEntry.avgPriceM2))
        ])
    });

    return {
        headline: "Rozkład oferty według lokalizacji",
        paragraphs
    };
};

const getPromotionsSubsection = (variables: IFlatVariables): ILocationGenericSubsection => {
    const paragraphs: ILocationGenericParagraph[] = [];

    if (variables.promotionsCount > 0) {
        paragraphs.push({
            type: ParagraphType.TEXT,
            text: `Aktualnie na rynku pierwotnym ${variables.regionNameDeclensionWhere} ${pluralize([
                `obowiązuje ${numberFormat(variables.promotionsCount)} oferta promocyjna`,
                `obowiązują ${numberFormat(variables.promotionsCount)} oferty promocyjne`,
                `obowiązuje ${numberFormat(variables.promotionsCount)} ofert promocyjnych`
            ])(variables.promotionsCount)}.`
        });
    } else {
        paragraphs.push({
            type: ParagraphType.TEXT,
            text: `Aktualnie na rynku pierwotnym ${variables.regionNameDeclensionWhere} nie obowiązują oferty promocyjne na mieszkania.`
        });
    }

    paragraphs.push({
        type: ParagraphType.TEXT,
        text: "Zapisz się do newslettera, aby otrzymać powiadomienie o obniżkach cen w tym regionie."
    });

    return {
        headline: "Aktualne promocje",
        paragraphs
    };
};
import {upperFirst} from "@pg-mono/nodash";
import {rpAppLink} from "@pg-mono/rp-routes";
import {areaFormat, dateTimeFormat, formatPL, numberFormat, pluralize, priceFormat, quarterToRomanNumerals} from "@pg-mono/string-utils";

import {Country} from "../../../../region/types/Country";
import {RegionType} from "../../../../region/types/RegionType";
import {
    ILocationGenericParagraph,
    ILocationGenericSection,
    ILocationGenericSectionProps,
    ILocationGenericSubsection,
    ParagraphType,
    TextType
} from "./constants";

const formatQuarterWithYear = (dateStr: string): string => {
    const date = new Date(dateStr);
    return quarterToRomanNumerals(formatPL(date, dateTimeFormat.quarterLong), "kwartał");
};

export const flatOffersLocationGenericDescription = (props: ILocationGenericSectionProps): ILocationGenericSection[] => {
    const variables = {
        regionName: props.region.name,
        regionSlug: props.region.slug,
        isCity: props.region.type === RegionType.CITY,
        regionNameDeclensionAbout: props.region.name_declension_about,
        regionNameDeclensionWhere: props.region.name_declension_where,
        regionPropertiesCountForSale: props.region.stats?.properties_all_count_for_sale || 0,
        regionOfferCountForSale: props.region.stats?.offers_count_for_sale || 0,
        propertiesFlatsRangesAreaMin: props.region.stats?.properties_flats_ranges_area_min || 0,
        propertiesFlatsRangesAreaMax: props.region.stats?.properties_flats_ranges_area_max || 0,
        promotionsCount: props.region.stats?.promotions_count_total || 0,
        averagePriceM2: props.region.stats?.average_price_m2 || 0,
        averagePriceM2Y2YChange: props.region.stats?.average_price_m2_y2y_change ?? null,
        flatPriceMin: props.region.stats?.properties_flats_ranges_price_min || 0,
        flatsCountReady: props.region.stats?.properties_flats_count_ready || 0,
        flatsCountReadyInThreeMonths: props.region.stats?.properties_flats_count_ready_in_3m || 0,
        flatsCountReadyInSixMonths: props.region.stats?.properties_flats_count_ready_in_6m || 0,
        flatsCountReadyInOneYear: props.region.stats?.properties_flats_count_ready_in_12m || 0,
        flatsGroundFloor: props.region.stats?.properties_flats_ground_floor || 0,
        flatsGroundFloorWithGarden: props.region.stats?.properties_flats_ground_floor_with_garden || 0,
        flatsLowFloor: props.region.stats?.properties_flats_low_floor || 0,
        flatsHighFloor: props.region.stats?.properties_flats_high_floor || 0,
        flatsLastFloor: props.region.stats?.properties_flats_last_floor || 0,
        flatsLastFloorWithTerrace: props.region.stats?.properties_flats_last_floor_with_terrace || 0,
        flatsLatestConstructionDate: props.region.stats?.properties_flats_latest_construction_date || null,
        flatsOffersCountForSale: props.region.stats?.offers_flats_count_for_sale || 0,
        flatsPropertiesCountForSale: props.region.stats?.properties_flats_count_for_sale || 0,
        flatsCountByDistrict: props.region.stats?.properties_flats_count_by_district || {},
        flatsCountByPrice: props.region.stats?.properties_flats_count_by_price || {},
        flatsAreaByRooms: props.region.stats?.properties_flats_ranges_area_by_rooms || {},
        flatsPriceByRooms: props.region.stats?.properties_flats_ranges_price_by_rooms || {},
        flatsPriceM2ByRooms: props.region.stats?.properties_flats_ranges_price_m2_by_rooms || {},
        housesPropertiesCountForSale: props.region.stats?.properties_houses_count_for_sale || 0,
        flatsRoomsRanges: props.region.stats?.properties_flats_rooms_ranges || {}
    };

    if (!variables.flatsOffersCountForSale || !variables.flatsPropertiesCountForSale) {
        return [];
    }

    if (props.region.country === Country.SPAIN) {
        return [
            {
                headline: `${variables.regionName} - mieszkanie w Hiszpanii`,
                paragraphs: [
                    {
                        type: ParagraphType.TEXT,
                        text: `W ${upperFirst(variables.regionName)} ${pluralize([
                            `dostępna jest ${numberFormat(variables.regionPropertiesCountForSale)} oferta mieszkania.`,
                            `dostępne są ${numberFormat(variables.regionPropertiesCountForSale)} oferty mieszkań.`,
                            `dostępnych jest ${numberFormat(variables.regionPropertiesCountForSale)} ofert mieszkań.`
                        ])(variables.regionPropertiesCountForSale)} Apartamenty w ${variables.regionName} mają powierzchnie ${
                            variables.propertiesFlatsRangesAreaMin !== variables.propertiesFlatsRangesAreaMax
                                ? pluralize([
                                      `${areaFormat(variables.propertiesFlatsRangesAreaMin)}`,
                                      `od ${variables.propertiesFlatsRangesAreaMin} do ${areaFormat(variables.propertiesFlatsRangesAreaMax)}`,
                                      `od ${variables.propertiesFlatsRangesAreaMin} do ${areaFormat(variables.propertiesFlatsRangesAreaMax)}`
                                  ])(variables.regionPropertiesCountForSale)
                                : `od ${areaFormat(variables.propertiesFlatsRangesAreaMin)}`
                        }.`
                    }
                ]
            },
            {
                headline: `Ile kosztuje nowe mieszkanie w Hiszpanii w ${variables.regionName}?`,
                paragraphs: [
                    {
                        type: ParagraphType.TEXT,
                        text: [
                            `Najtańsze mieszkanie w ${variables.regionName} w sprzedaży kosztuje ${priceFormat(variables.flatPriceMin, {unit: "euro"})}.`,
                            variables.averagePriceM2 > 0
                                ? `Średnia cena za m2 mieszkania w ${variables.regionName} wynosi ${priceFormat(variables.averagePriceM2, {unit: "euro"})}.`
                                : ""
                        ]
                    },
                    {
                        type: ParagraphType.TEXT,
                        text: [
                            `Jesteś zainteresowany zakupem domu w Hiszpanii? Poznaj `,
                            variables.housesPropertiesCountForSale > 0
                                ? {
                                      type: TextType.LINK,
                                      href: rpAppLink.offer.listFriendlyAbroad({
                                          friendlySlug: `nowe-domy`,
                                          country: `hiszpania`,
                                          abroadRegionSlug: `${variables.regionSlug}`
                                      }),
                                      text: `ofertę domów w ${variables.regionName}.`
                                  }
                                : `ofertę domów ${variables.regionName}.`
                        ]
                    }
                ]
            }
        ];
    }

    const now = new Date();
    const currentMonth = formatPL(now, "LLLL");
    const currentYear = now.getFullYear();
    const monthYear = `${currentMonth} ${currentYear}`;
    const updateDate = formatPL(now, "dd.MM.yyyy");

    // Section 1: Available offer
    const availableOfferSubsection = getAvailableOfferSubsection(variables, monthYear);

    // Section 2: Prices and available sizes
    const pricesSubsection = getPricesSubsection(variables, monthYear);

    // Section 3: Delivery dates
    const deliverySubsection = getDeliverySubsection(variables, monthYear);

    // Section 4: Price ranges
    const priceRangesSubsection = getPriceRangesSubsection(variables, monthYear);

    // Section 5: Geographic distribution
    const geographicDistributionSubsection = getGeographicDistributionSubsection(variables, monthYear);

    // Section 6: Promotions
    const promotionsSubsection = getPromotionsSubsection(variables);

    const subsections: ILocationGenericSubsection[] = [
        availableOfferSubsection,
        pricesSubsection,
        deliverySubsection,
        ...(priceRangesSubsection ? [priceRangesSubsection] : []),
        ...(geographicDistributionSubsection ? [geographicDistributionSubsection] : []),
        promotionsSubsection
    ];

    return [
        {
            headline: `Raport rynkowy – mieszkania ${variables.regionName}, ${monthYear}`,
            description: `Sprawdź aktualny stan rynku nowych mieszkań ${variables.regionNameDeclensionWhere}: liczba ofert, ceny, metraże oraz terminy oddania inwestycji deweloperskich.`,
            updateDate,
            paragraphs: [],
            subsections
        }
    ];
};

interface IFlatVariables {
    regionName: string;
    regionSlug: string;
    isCity: boolean;
    regionNameDeclensionAbout: string;
    regionNameDeclensionWhere: string;
    regionPropertiesCountForSale: number;
    regionOfferCountForSale: number;
    propertiesFlatsRangesAreaMin: number;
    propertiesFlatsRangesAreaMax: number;
    promotionsCount: number;
    averagePriceM2: number;
    averagePriceM2Y2YChange: number | null;
    flatPriceMin: number;
    flatsCountReady: number;
    flatsCountReadyInThreeMonths: number;
    flatsCountReadyInSixMonths: number;
    flatsCountReadyInOneYear: number;
    flatsGroundFloor: number;
    flatsGroundFloorWithGarden: number;
    flatsLowFloor: number;
    flatsHighFloor: number;
    flatsLastFloor: number;
    flatsLastFloorWithTerrace: number;
    flatsLatestConstructionDate: string | null;
    flatsOffersCountForSale: number;
    flatsPropertiesCountForSale: number;
    flatsCountByDistrict: Record<string, IFlatDistrictStats>;
    flatsCountByPrice: Record<string, number>;
    flatsAreaByRooms: Record<string, IFlatRangeStats>;
    flatsPriceByRooms: Record<string, IFlatRangeStats>;
    flatsPriceM2ByRooms: Record<string, IFlatRangeStats>;
    housesPropertiesCountForSale: number;
    flatsRoomsRanges: Record<number, number>;
}

interface IFlatRangeStats {
    avg?: number;
    max: number;
    min: number;
}

interface IFlatDistrictStats {
    avg_price_m2: number;
    count: number;
}

const getPriceTableRoomLabel = (roomCount: number): string => {
    switch (roomCount) {
        case 1:
            return "Kawalerki";
        case 2:
            return "2 pokoje";
        case 3:
            return "3 pokoje";
        case 4:
            return "4 pokoje";
        case 5:
            return "5+ pokoi";
        default:
            return `${roomCount} pokoi`;
    }
};

const getRoomTypeLabel = (roomCount: number): string => {
    switch (roomCount) {
        case 1:
            return "Kawalerki";
        case 2:
            return "Mieszkania 2-pokojowe";
        case 3:
            return "Mieszkania 3-pokojowe";
        case 4:
            return "Mieszkania 4-pokojowe";
        case 5:
            return "Mieszkania 5-pokojowe i większe";
        default:
            return `Mieszkania ${roomCount}-pokojowe`;
    }
};

const isFiniteNumber = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);

const getAveragePriceM2Y2YChangeText = (variables: IFlatVariables): string | null => {
    if (!variables.isCity || !isFiniteNumber(variables.averagePriceM2Y2YChange)) {
        return null;
    }

    if (variables.averagePriceM2Y2YChange === 0) {
        return `Zmiana rok do roku: Ceny nowych mieszkań ${variables.regionNameDeclensionWhere} nie zmieniły się w porównaniu rok do roku.`;
    }

    return `Zmiana rok do roku: Ceny nowych mieszkań ${variables.regionNameDeclensionWhere} ${
        variables.averagePriceM2Y2YChange > 0 ? "wzrosły" : "spadły"
    } o ${numberFormat(Math.abs(variables.averagePriceM2Y2YChange), {precision: 2})} % w porównaniu rok do roku.`;
};

const ROOM_PRICE_TABLE_ROOM_COUNTS = [1, 2, 3, 4, 5];
const MIN_GROUPED_ROOM_COUNT = 5;

const getRoomRangeStats = (record: Record<string, IFlatRangeStats>, roomCount: number): IFlatRangeStats | null => {
    const roomKey = roomCount === 5 ? "5+" : String(roomCount);
    const value = record[roomKey as keyof typeof record];

    if (value && isFiniteNumber(value.avg) && isFiniteNumber(value.max) && isFiniteNumber(value.min)) {
        return value;
    }

    return null;
};

const getRoomRangeCount = (roomRanges: Record<number, number>, roomCount: number): number => {
    if (roomCount < MIN_GROUPED_ROOM_COUNT) {
        return roomRanges[roomCount] || 0;
    }

    return Object.entries(roomRanges).reduce((totalCount, [roomRange, count]) => {
        const parsedRoomRange = Number(roomRange);

        if (!isFiniteNumber(count) || !Number.isFinite(parsedRoomRange) || parsedRoomRange < MIN_GROUPED_ROOM_COUNT) {
            return totalCount;
        }

        return totalCount + count;
    }, 0);
};

const getPricesByRoomRows = (variables: IFlatVariables): string[][] => {
    const rows = ROOM_PRICE_TABLE_ROOM_COUNTS.map((roomCount) => {
        const areaStats = getRoomRangeStats(variables.flatsAreaByRooms, roomCount);
        const priceM2Stats = getRoomRangeStats(variables.flatsPriceM2ByRooms, roomCount);
        const priceStats = getRoomRangeStats(variables.flatsPriceByRooms, roomCount);

        if (!areaStats?.avg || !priceM2Stats?.avg || !priceStats?.min || !priceStats?.avg) {
            return null;
        }

        return [
            getPriceTableRoomLabel(roomCount),
            String(numberFormat(areaStats.avg, {precision: 2})),
            String(numberFormat(priceM2Stats.avg)),
            String(numberFormat(priceStats.min)),
            String(numberFormat(priceStats.avg))
        ];
    }).filter(Boolean) as string[][];

    return rows.length === ROOM_PRICE_TABLE_ROOM_COUNTS.length ? rows : [];
};

const getAvailableOfferSubsection = (variables: IFlatVariables, monthYear: string): ILocationGenericSubsection => {
    const roomRanges = variables.flatsRoomsRanges;
    const totalFlats = variables.flatsPropertiesCountForSale;

    const roomEntries = [1, 2, 3, 4, 5].map((room) => {
        const count = getRoomRangeCount(roomRanges, room);

        return {
            room,
            count,
            percent: totalFlats > 0 ? ((count / totalFlats) * 100).toFixed(1) : "0.0"
        };
    });

    const hasRoomEntries = roomEntries.some((entry) => entry.count > 0);
    const dominantRoom = roomEntries.reduce((max, entry) => (entry.count > max.count ? entry : max), {room: 0, count: 0, percent: "0.0"});

    const paragraphs: ILocationGenericParagraph[] = [
        {
            type: ParagraphType.TEXT,
            text: `${upperFirst(variables.regionNameDeclensionWhere)} ${pluralize([
                `dostępna jest w sprzedaży ${numberFormat(variables.flatsPropertiesCountForSale)} oferta nowych mieszkań znajdująca`,
                `dostępne są w sprzedaży ${numberFormat(variables.flatsPropertiesCountForSale)} oferty nowych mieszkań znajdujące`,
                `dostępnych jest w sprzedaży ${numberFormat(variables.flatsPropertiesCountForSale)} ofert nowych mieszkań znajdujących`
            ])(variables.flatsPropertiesCountForSale)} się w ${pluralize([
                `${numberFormat(variables.flatsOffersCountForSale)} inwestycji deweloperskiej`,
                `${numberFormat(variables.flatsOffersCountForSale)} inwestycjach deweloperskich`,
                `${numberFormat(variables.flatsOffersCountForSale)} inwestycjach deweloperskich`
            ])(variables.flatsOffersCountForSale)}.`
        }
    ];

    if (dominantRoom.room > 0) {
        paragraphs.push({
            type: ParagraphType.TEXT,
            text: `W ofercie deweloperów ${variables.regionNameDeclensionWhere} dominują mieszkania ${dominantRoom.room === 1 ? "jednopokojowe" : `${dominantRoom.room}-pokojowe`}, które stanowią ${dominantRoom.percent} % wszystkich mieszkań.`
        });
    }

    if (hasRoomEntries) {
        paragraphs.push({
            type: ParagraphType.TABLE,
            caption: `Struktura oferty wg typu mieszkania – ${variables.regionName}, ${monthYear}`,
            captionHidden: "(dane z RynekPierwotny.pl)",
            headers: ["Typ mieszkania", "Liczba lokali", "Procent oferty deweloperów"],
            rows: roomEntries.map((entry) => [getRoomTypeLabel(entry.room), String(numberFormat(entry.count)), `${entry.percent} %`])
        });
    }

    return {
        headline: "Dostępna oferta mieszkań",
        paragraphs
    };
};

const getPricesSubsection = (variables: IFlatVariables, monthYear: string): ILocationGenericSubsection => {
    const paragraphs: ILocationGenericParagraph[] = [];
    const textParts: string[] = [];

    if (variables.flatPriceMin > 0) {
        textParts.push(`Najniższa cena: Najtańsze nowe mieszkanie ${variables.regionNameDeclensionWhere} kosztuje ${priceFormat(variables.flatPriceMin)}.`);
    }

    if (variables.averagePriceM2 > 0) {
        textParts.push(
            `Średnia cena za mkw.: Cena metra kwadratowego mieszkania na rynku pierwotnym ${variables.regionNameDeclensionWhere} wynosi ${priceFormat(variables.averagePriceM2)}.`
        );
    }

    const averagePriceM2Y2YChangeText = getAveragePriceM2Y2YChangeText(variables);

    if (averagePriceM2Y2YChangeText) {
        textParts.push(averagePriceM2Y2YChangeText);
    }

    if (variables.propertiesFlatsRangesAreaMin > 0 && variables.propertiesFlatsRangesAreaMax > 0) {
        textParts.push(
            variables.propertiesFlatsRangesAreaMin !== variables.propertiesFlatsRangesAreaMax
                ? `Rozpiętość metraży: Metraże lokali zaczynają się od ${areaFormat(variables.propertiesFlatsRangesAreaMin)} i sięgają ${areaFormat(variables.propertiesFlatsRangesAreaMax)}.`
                : `Rozpiętość metraży: Metraże lokali wynoszą ${areaFormat(variables.propertiesFlatsRangesAreaMin)}.`
        );
    }

    if (textParts.length > 0) {
        paragraphs.push({
            type: ParagraphType.LIST,
            text: textParts
        });
    }

    const pricesByRoomRows = getPricesByRoomRows(variables);

    if (pricesByRoomRows.length > 0) {
        paragraphs.push({
            type: ParagraphType.TABLE,
            caption: `Ceny i metraże nowych mieszkań według liczby pokoi – ${variables.regionName}, ${monthYear}`,
            captionHidden: "(dane z RynekPierwotny.pl)",
            headers: ["Liczba pokoi", "Średni metraż (mkw.)", "Średnia cena za mkw. (zł)", "Cena min. (zł)", "Cena średnia (zł)"],
            rows: pricesByRoomRows
        });
    }

    return {
        headline: "Ceny i dostępne metraże",
        paragraphs
    };
};

const getDeliverySubsection = (variables: IFlatVariables, monthYear: string): ILocationGenericSubsection => {
    const paragraphs: ILocationGenericParagraph[] = [];

    const totalInConstruction = variables.flatsCountReadyInThreeMonths + variables.flatsCountReadyInSixMonths + variables.flatsCountReadyInOneYear;

    if (variables.flatsCountReady > 0) {
        paragraphs.push({
            type: ParagraphType.TEXT,
            text: `Aktualnie ${variables.regionNameDeclensionWhere} ${pluralize([
                `dostępna jest ${numberFormat(variables.flatsCountReady)} oferta`,
                `dostępne są ${numberFormat(variables.flatsCountReady)} oferty`,
                `dostępnych jest ${numberFormat(variables.flatsCountReady)} ofert`
            ])(variables.flatsCountReady)} nowych mieszkań gotowych do odbioru.`
        });
    } else {
        paragraphs.push({
            type: ParagraphType.TEXT,
            text: `Obecnie w ofercie deweloperów ${variables.regionNameDeclensionWhere} nie ma mieszkań oddanych do użytku.${
                variables.flatsLatestConstructionDate
                    ? ` Najpóźniejszy planowany termin oddania inwestycji do użytkowania to koniec ${formatQuarterWithYear(variables.flatsLatestConstructionDate)}.`
                    : ""
            }`
        });
    }

    if (totalInConstruction > 0) {
        paragraphs.push({
            type: ParagraphType.TEXT,
            text: `W trakcie budowy ${pluralize([
                `jest ${numberFormat(totalInConstruction)} mieszkanie`,
                `są ${numberFormat(totalInConstruction)} mieszkania`,
                `jest ${numberFormat(totalInConstruction)} mieszkań`
            ])(totalInConstruction)}.${
                variables.flatsLatestConstructionDate
                    ? ` Najpóźniejszy planowany termin oddania inwestycji do użytkowania to koniec ${formatQuarterWithYear(variables.flatsLatestConstructionDate)}.`
                    : ""
            }`
        });
    }

    const deliveryRows: string[][] = [];

    if (variables.flatsCountReady > 0) {
        deliveryRows.push(["Gotowe do odbioru", String(numberFormat(variables.flatsCountReady))]);
    }
    if (variables.flatsCountReadyInSixMonths > 0) {
        deliveryRows.push(["Oddanie do 6 miesięcy", String(numberFormat(variables.flatsCountReadyInSixMonths))]);
    }
    if (variables.flatsCountReadyInOneYear > 0) {
        deliveryRows.push(["Oddanie od 6 do 12 miesięcy", String(numberFormat(variables.flatsCountReadyInOneYear - variables.flatsCountReadyInSixMonths))]);
    }

    const beyondOneYear = variables.flatsPropertiesCountForSale - variables.flatsCountReady - variables.flatsCountReadyInOneYear;
    if (beyondOneYear > 0) {
        deliveryRows.push(["Oddanie powyżej roku", String(numberFormat(beyondOneYear))]);
    }

    if (deliveryRows.length > 0) {
        paragraphs.push({
            type: ParagraphType.TABLE,
            caption: `Dostępność mieszkań według terminu oddania – ${variables.regionName}, ${monthYear}`,
            captionHidden: "(dane z RynekPierwotny.pl)",
            headers: ["Termin oddania do użytku", "Liczba lokali"],
            rows: deliveryRows
        });
    }

    return {
        headline: "Terminy oddania do użytku",
        paragraphs
    };
};

interface ICountEntry {
    label: string;
    count: number;
    order: number;
}

interface IDistrictEntry {
    avgPriceM2: number;
    count: number;
    label: string;
}

const PRICE_RANGES = [
    {
        label: "Poniżej 400 000 zł",
        keys: ["up400k", "up400000", "up_400000", "0-400000", "0_400000", "below_400000", "less_than_400000", "lt_400000", "ponizej_400000"]
    },
    {label: "400 001 – 600 000 zł", keys: ["400k600k", "400001-600000", "400001_600000", "400000-600000", "400000_600000"]},
    {label: "600 001 – 800 000 zł", keys: ["600k800k", "600001-800000", "600001_800000", "600000-800000", "600000_800000"]},
    {label: "800 001 – 1 000 000 zł", keys: ["800k1m", "800001-1000000", "800001_1000000", "800000-1000000", "800000_1000000"]},
    {
        label: "Powyżej 1 000 000 zł",
        keys: [
            "over1m",
            "over1000000",
            "over_1000000",
            "1000001-",
            "1000001_plus",
            "1000000-",
            "1000000_plus",
            "above_1000000",
            "gt_1000000",
            "powyzej_1000000"
        ]
    }
];

const CUMULATIVE_PRICE_THRESHOLDS = [
    {threshold: 400000, keys: ["up400k", "up400000", "up_400000"]},
    {threshold: 600000, keys: ["up600k", "up600000", "up_600000"]},
    {threshold: 800000, keys: ["up800k", "up800000", "up_800000"]},
    {threshold: 1000000, keys: ["up1m", "up1000k", "up1000000", "up_1000000"]}
] as const;

const CUMULATIVE_PRICE_RANGES = [
    {label: "400 001 – 600 000 zł", lowerThreshold: 400000, upperThreshold: 600000},
    {label: "600 001 – 800 000 zł", lowerThreshold: 600000, upperThreshold: 800000},
    {label: "800 001 – 1 000 000 zł", lowerThreshold: 800000, upperThreshold: 1000000}
];

const normalizeCountKey = (key: string): string =>
    key
        .toLowerCase()
        .replace(/[–—]/g, "-")
        .replace(/\u00a0/g, " ")
        .replace(/\s+/g, "")
        .replace(/zł/g, "zl")
        .replace(/więcej/g, "plus")
        .replace(/powyżej/g, "powyzej")
        .replace(/poniżej/g, "ponizej");

const getPriceRangeLabel = (key: string): string | null => {
    const normalizedKey = normalizeCountKey(key);
    const priceRange = PRICE_RANGES.find(
        (range) => normalizeCountKey(range.label) === normalizedKey || range.keys.some((rangeKey) => normalizeCountKey(rangeKey) === normalizedKey)
    );

    return priceRange?.label || null;
};

const getPriceRangeOrder = (label: string): number => {
    const index = PRICE_RANGES.findIndex((range) => range.label === label || normalizeCountKey(range.label) === normalizeCountKey(label));

    return index === -1 ? Number.MAX_SAFE_INTEGER : index;
};

const getCumulativePriceThresholdCount = (counts: Record<string, number>, threshold: number): number | null => {
    const thresholdConfig = CUMULATIVE_PRICE_THRESHOLDS.find((priceThreshold) => priceThreshold.threshold === threshold);

    if (!thresholdConfig) {
        return null;
    }

    const normalizedThresholdKeys = thresholdConfig.keys.map(normalizeCountKey);
    const thresholdEntry = Object.entries(counts).find(
        ([key, count]) => normalizedThresholdKeys.includes(normalizeCountKey(key)) && isFiniteNumber(count) && count >= 0
    );

    return thresholdEntry?.[1] ?? null;
};

const sortCountEntries = (entries: ICountEntry[]): ICountEntry[] =>
    entries.sort((firstEntry, secondEntry) => {
        if (firstEntry.order !== secondEntry.order) {
            return firstEntry.order - secondEntry.order;
        }

        return firstEntry.label.localeCompare(secondEntry.label, "pl");
    });

const getCountEntries = (counts: Record<string, number>, getLabel: (key: string) => string | null, getOrder: (label: string) => number): ICountEntry[] => {
    const entries = new Map<string, ICountEntry>();

    Object.entries(counts).forEach(([key, count]) => {
        if (!isFiniteNumber(count) || count < 0) {
            return;
        }

        const label = getLabel(key);

        if (!label) {
            return;
        }

        const currentEntry = entries.get(label);

        entries.set(label, {
            label,
            count: (currentEntry?.count || 0) + count,
            order: getOrder(label)
        });
    });

    return sortCountEntries(Array.from(entries.values()));
};

const getPriceRangeEntries = (counts: Record<string, number>): ICountEntry[] => {
    const entriesByLabel = new Map(getCountEntries(counts, getPriceRangeLabel, getPriceRangeOrder).map((entry) => [entry.label, entry]));

    CUMULATIVE_PRICE_RANGES.forEach((priceRange) => {
        if (entriesByLabel.has(priceRange.label)) {
            return;
        }

        const lowerCount = getCumulativePriceThresholdCount(counts, priceRange.lowerThreshold);
        const upperCount = getCumulativePriceThresholdCount(counts, priceRange.upperThreshold);

        if (lowerCount === null || upperCount === null || upperCount < lowerCount) {
            return;
        }

        entriesByLabel.set(priceRange.label, {
            label: priceRange.label,
            count: upperCount - lowerCount,
            order: getPriceRangeOrder(priceRange.label)
        });
    });

    return sortCountEntries(Array.from(entriesByLabel.values()));
};

const getPriceRangesSubsection = (variables: IFlatVariables, monthYear: string): ILocationGenericSubsection | null => {
    const priceRangeEntries = getPriceRangeEntries(variables.flatsCountByPrice);

    if (priceRangeEntries.length === 0) {
        return null;
    }

    const paragraphs: ILocationGenericParagraph[] = [];
    const mostPopularRange = priceRangeEntries.reduce<ICountEntry | null>(
        (currentMostPopularRange, priceRangeEntry) =>
            currentMostPopularRange && currentMostPopularRange.count >= priceRangeEntry.count ? currentMostPopularRange : priceRangeEntry,
        null
    );

    paragraphs.push({
        type: ParagraphType.TABLE,
        caption: `Liczba mieszkań według przedziałów cenowych – ${variables.regionName}, ${monthYear}`,
        captionHidden: "(dane z RynekPierwotny.pl)",
        headers: ["Zakres cenowy", "Liczba lokali"],
        rows: priceRangeEntries.map((priceRangeEntry) => [priceRangeEntry.label, String(numberFormat(priceRangeEntry.count))])
    });

    if (mostPopularRange && mostPopularRange.count > 0) {
        paragraphs.push({
            type: ParagraphType.TEXT,
            text: `Najwięcej ofert nowych mieszkań ${variables.regionNameDeclensionWhere} jest dostępnych w przedziale ${mostPopularRange.label}.`
        });
    }

    return {
        headline: "Rozkład oferty według przedziałów cenowych",
        paragraphs
    };
};

const getDistrictEntries = (districts: Record<string, IFlatDistrictStats>): IDistrictEntry[] =>
    Object.entries(districts)
        .filter(([, districtStats]) => isFiniteNumber(districtStats.count) && districtStats.count > 0 && isFiniteNumber(districtStats.avg_price_m2))
        .map(([districtName, districtStats]) => ({
            avgPriceM2: districtStats.avg_price_m2,
            count: districtStats.count,
            label: districtName
        }))
        .sort((firstEntry, secondEntry) => firstEntry.label.localeCompare(secondEntry.label, "pl"));

const getGeographicDistributionSubsection = (variables: IFlatVariables, monthYear: string): ILocationGenericSubsection | null => {
    const districtEntries = getDistrictEntries(variables.flatsCountByDistrict);

    if (districtEntries.length === 0) {
        return null;
    }

    const paragraphs: ILocationGenericParagraph[] = [];
    const topDistrict = districtEntries.reduce((currentTopDistrict, districtEntry) =>
        currentTopDistrict.count >= districtEntry.count ? currentTopDistrict : districtEntry
    );
    const cheapestDistrict = districtEntries.reduce((currentCheapestDistrict, districtEntry) =>
        currentCheapestDistrict.avgPriceM2 <= districtEntry.avgPriceM2 ? currentCheapestDistrict : districtEntry
    );

    paragraphs.push({
        type: ParagraphType.TEXT,
        text: `Największy wybór nowych lokali ${variables.regionNameDeclensionWhere} deweloperzy oferują w dzielnicy ${topDistrict.label}, gdzie ${pluralize([
            `dostępna jest ${numberFormat(topDistrict.count)} oferta`,
            `dostępne są ${numberFormat(topDistrict.count)} oferty`,
            `dostępnych jest ${numberFormat(topDistrict.count)} ofert`
        ])(topDistrict.count)}. Najniższa średnia cena obowiązuje w dzielnicy ${cheapestDistrict.label}.`
    });

    paragraphs.push({
        type: ParagraphType.TABLE,
        caption: `Liczba ofert i średnie ceny według dzielnic – ${variables.regionName}, ${monthYear}`,
        captionHidden: "(dane z RynekPierwotny.pl)",
        headers: ["Lokalizacja inwestycji", "Liczba lokali", "Średnia cena zł/mkw."],
        rows: districtEntries.map((districtEntry) => [
            districtEntry.label,
            String(numberFormat(districtEntry.count)),
            String(numberFormat(districtEntry.avgPriceM2))
        ])
    });

    return {
        headline: "Rozkład oferty według lokalizacji",
        paragraphs
    };
};

const getPromotionsSubsection = (variables: IFlatVariables): ILocationGenericSubsection => {
    const paragraphs: ILocationGenericParagraph[] = [];

    if (variables.promotionsCount > 0) {
        paragraphs.push({
            type: ParagraphType.TEXT,
            text: `Aktualnie na rynku pierwotnym ${variables.regionNameDeclensionWhere} ${pluralize([
                `obowiązuje ${numberFormat(variables.promotionsCount)} oferta promocyjna`,
                `obowiązują ${numberFormat(variables.promotionsCount)} oferty promocyjne`,
                `obowiązuje ${numberFormat(variables.promotionsCount)} ofert promocyjnych`
            ])(variables.promotionsCount)}.`
        });
    } else {
        paragraphs.push({
            type: ParagraphType.TEXT,
            text: `Aktualnie na rynku pierwotnym ${variables.regionNameDeclensionWhere} nie obowiązują oferty promocyjne na mieszkania.`
        });
    }

    paragraphs.push({
        type: ParagraphType.TEXT,
        text: "Zapisz się do newslettera, aby otrzymać powiadomienie o obniżkach cen w tym regionie."
    });

    return {
        headline: "Aktualne promocje",
        paragraphs
    };
};
